import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Container, Content, ScrollButton } from './ScrollSlider.styles';

type Props = PropsWithChildren<{
  className?: string;
}>;

const ScrollSlider = ({ className, children }: Props): JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState<'left' | 'middle' | 'right'>('left');
  const [scrollEnabled, setScrollEnabled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = (event: Event): void => {
      const element = event.target as HTMLDivElement;

      if (element.scrollLeft === 0) {
        setScrollPosition('left');
      } else if (Math.ceil(element.scrollLeft) + element.offsetWidth === element.scrollWidth) {
        setScrollPosition('right');
      } else {
        setScrollPosition('middle');
      }
    };

    const refElement = contentRef.current;

    if (refElement) {
      setScrollEnabled(refElement.offsetWidth !== refElement.scrollWidth);
    }

    refElement?.addEventListener('scroll', handleScroll);

    return () => {
      refElement?.removeEventListener('scroll', handleScroll);
    };
  }, [contentRef, setScrollPosition, setScrollEnabled]);

  const scrollTo = (to: 'left' | 'right') => (): void => {
    contentRef.current?.scrollTo({
      left: to === 'left' ? 0 : contentRef.current?.scrollWidth,
      behavior: 'smooth',
    });
  };

  return (
    <Container
      className={className}
      $fadeLeft={scrollEnabled && scrollPosition !== 'left'}
      $fadeRight={scrollEnabled && scrollPosition !== 'right'}
    >
      <Content ref={contentRef}>{children}</Content>
      <ScrollButton
        $position="left"
        disabled={!scrollEnabled || scrollPosition === 'left'}
        onClick={scrollTo('left')}
      >
        <KeyboardArrowLeftIcon />
      </ScrollButton>
      <ScrollButton
        $position="right"
        disabled={!scrollEnabled || scrollPosition === 'right'}
        onClick={scrollTo('right')}
      >
        <KeyboardArrowRightIcon />
      </ScrollButton>
    </Container>
  );
};

export default ScrollSlider;
